import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { FeatureCodeValues } from '@/api/generated/enums';
import { useNotify } from '@/api/notify';
import { Popconfirm } from 'antd';
import { enterKeyScope } from '@/components/enterKeyNavigation';
import { controlClass, Field } from '@/components/Field';
import { Button, Card, Notice, SectionTitle } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate } from '@core/format/dates';

import {
  applyStarterCatalogue,
  clearFeatureOverride,
  createBranch,
  createFinancialYear,
  createStandardSeries,
  goLive,
  platformKeys,
  setFeatureOverride,
  useCompanies,
  useCompanyBranches,
  useCompanyNumbering,
  useCompanyYears,
  useFeatureOverrides,
  useOnboarding,
  usePlans,
  useStarterCatalogue,
  type BranchInput,
  type StarterCategory,
} from './api';
import { BillingSection } from './BillingSection';
import { dropSetupSession } from './client';
import { suggestFinancialYear } from './financialYear';
import { featuresWithScreens, moduleRows, orderRows } from './modules';

const stepLabels: Record<string, string> = {
  CompanyDetails: 'Company details',
  Users: 'Owner account',
  Branches: 'Branches',
  FinancialYear: 'Financial year',
  DocumentNumbering: 'Document numbering',
  MasterData: 'Catalogue and garments',
  GoLive: 'Live',
};

/**
 * One company, set up in order. Everything below the package is the company's own data, written
 * through its own endpoints by a setup session — and only while the company has not gone live, after
 * which those sections close and only billing and the package remain editable from here. Billing
 * leads the page: what a company owes is platform data, never the company's own, so going live
 * closes nothing about it.
 */
export function CompanyPage({
  tenantId,
  canWrite,
  onBack,
  onSetTerms,
  onRecordPayment,
  onReceipt,
}: {
  tenantId: number;
  canWrite: boolean;
  onBack: () => void;
  onSetTerms: () => void;
  onRecordPayment: () => void;
  onReceipt: (paymentId: number) => void;
}) {
  const onboarding = useOnboarding(tenantId);
  const company = useCompanies().data?.find((c) => c.tenantId === tenantId);
  const live = onboarding.data?.isLive ?? false;
  const setup = canWrite && onboarding.isSuccess && !live;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to companies"
            className="text-default flex size-8 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
          >
            <i className="icon icon-arrow-left" />
          </button>
          <div>
            <h1 className="page-title">{onboarding.data?.tenantName ?? 'Company'}</h1>
            <p className="page-subtitle font-mono">{onboarding.data?.tenantCode}</p>
          </div>
        </div>
        {onboarding.data &&
          (live ? (
            <StatusBadge tone="positive">
              Live since {formatDate(onboarding.data.wentLiveOn)}
            </StatusBadge>
          ) : (
            <StatusBadge tone="warning">In setup</StatusBadge>
          ))}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 flex flex-col gap-4 xl:col-span-8">
          {live && (
            <Notice title="This company is live, so setup access has ended">
              Its branches, year and catalogue are now the shop&apos;s to change. Support access
              needs the customer&apos;s consent; billing, the package and modules can still be
              changed here.
            </Notice>
          )}
          {!canWrite && (
            <Notice title="Read-only">
              Only a super-admin can change a company from the panel.
            </Notice>
          )}
          <BillingSection
            tenantId={tenantId}
            canWrite={canWrite}
            onSetTerms={onSetTerms}
            onRecordPayment={onRecordPayment}
            onReceipt={onReceipt}
          />
          <ModulesSection
            tenantId={tenantId}
            planCode={company?.planCode ?? null}
            canWrite={canWrite}
          />
          {setup && (
            <>
              <BranchesSection tenantId={tenantId} />
              <YearAndNumberingSection tenantId={tenantId} />
              <CatalogueSection tenantId={tenantId} />
            </>
          )}
        </div>

        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          <Card>
            <SectionTitle icon="icon icon-list-checks" hue="indigo">
              Setup checklist
            </SectionTitle>
            <ul className="flex flex-col gap-2 ps-0">
              {(onboarding.data?.steps ?? []).map((step) => (
                <li key={step.step} className="flex list-none gap-2">
                  <i
                    className={`icon mt-0.5 ${step.isComplete ? 'icon-circle-check text-success' : 'icon-circle-dashed text-default'}`}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">
                      {stepLabels[step.step] ?? step.step}
                    </span>
                    {step.detail && (
                      <span className="text-default block text-[length:var(--scale-field-hint)]">
                        {step.detail}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
          {setup && onboarding.data && (
            <GoLiveCard tenantId={tenantId} steps={onboarding.data.steps} />
          )}
        </div>
      </div>
    </>
  );
}

function useRefresh(tenantId: number) {
  const queryClient = useQueryClient();
  return (...keys: (readonly unknown[])[]) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: platformKeys.onboarding(tenantId) }),
      queryClient.invalidateQueries({ queryKey: platformKeys.companies }),
      ...keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ]);
}

// ---- package & modules ------------------------------------------------------------------------

function ModulesSection({
  tenantId,
  planCode,
  canWrite,
}: {
  tenantId: number;
  planCode: string | null;
  canWrite: boolean;
}) {
  const notify = useNotify();
  const refresh = useRefresh(tenantId);
  const plans = usePlans();
  const overrides = useFeatureOverrides(tenantId);
  const [reason, setReason] = useState('Pilot setup');
  const [busy, setBusy] = useState<string | null>(null);

  const plan = plans.data?.find((p) => p.code === planCode);
  const rows = orderRows(
    moduleRows(plan, overrides.data ?? [], featuresWithScreens(), FeatureCodeValues),
  );

  const change = async (feature: (typeof rows)[number]) => {
    setBusy(feature.feature);
    try {
      if (feature.override) {
        await clearFeatureOverride(tenantId, feature.feature);
      } else {
        await setFeatureOverride(tenantId, feature.feature, !feature.effective, reason.trim());
      }
      await refresh(platformKeys.overrides(tenantId));
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <SectionTitle
        icon="icon icon-toggle-right"
        hue="purple"
        trailing={plan && <StatusBadge tone="brand">{plan.name}</StatusBadge>}
      >
        Package and modules
      </SectionTitle>
      <p className="text-default mb-3 text-[length:var(--scale-field-hint)]">
        What staff see is the package&apos;s modules plus anything switched on here, minus anything
        switched off — and then only what each person&apos;s role allows.
      </p>
      {canWrite && (
        <Field label="Reason recorded with each change" className="mb-3 max-w-md">
          <input
            className={controlClass}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      )}
      <ul className="divide-border-color flex flex-col divide-y ps-0">
        {rows.map((row) => (
          <li
            key={row.feature}
            className="flex list-none flex-wrap items-center justify-between gap-2 py-2"
          >
            <span className="flex flex-wrap items-center gap-2">
              <i
                className={`icon ${row.effective ? 'icon-circle-check text-success' : 'icon-circle-dashed text-default'}`}
              />
              <span className="text-sm text-gray-900">{row.label}</span>
              {row.override ? (
                <StatusBadge tone={row.override.isGranted ? 'brand' : 'warning'}>
                  {row.override.isGranted ? 'Switched on here' : 'Switched off here'}
                </StatusBadge>
              ) : row.inPackage ? (
                <StatusBadge tone="neutral">In the package</StatusBadge>
              ) : null}
              {!row.hasScreens && <StatusBadge tone="neutral">No screens yet</StatusBadge>}
            </span>
            {canWrite && row.hasScreens && (
              <Button
                disabled={busy !== null || (!row.override && !reason.trim())}
                onClick={() => void change(row)}
              >
                {busy === row.feature
                  ? 'Saving…'
                  : row.override
                    ? 'Back to the package'
                    : row.effective
                      ? 'Switch off'
                      : 'Switch on'}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---- branches ---------------------------------------------------------------------------------

const blankBranch: BranchInput = {
  code: '',
  name: '',
  isHeadOffice: false,
  gstin: '',
  stateCode: '',
  stateName: '',
  addressLine1: '',
  city: '',
  pincode: '',
  contactPhone: '',
};

function BranchesSection({ tenantId }: { tenantId: number }) {
  const notify = useNotify();
  const refresh = useRefresh(tenantId);
  const branches = useCompanyBranches(tenantId, true);
  const first = (branches.data?.length ?? 0) === 0;
  const [draft, setDraft] = useState<BranchInput>(blankBranch);
  const [busy, setBusy] = useState(false);
  // No autofocus here, deliberately: this page is a hub of several forms, and focusing the branch
  // form on open scrolled straight past the checklist and the package — found by driving it.

  const set = (key: keyof BranchInput) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({
      ...d,
      [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    }));

  const add = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(
        Object.entries(draft).map(([k, v]) => [
          k,
          typeof v === 'string' ? v.trim() || undefined : v,
        ]),
      ) as unknown as BranchInput;
      // The first branch is the head office whatever the box says — a company has to have one.
      await createBranch(tenantId, {
        ...clean,
        code: draft.code.trim().toUpperCase(),
        isHeadOffice: first || draft.isHeadOffice,
      });
      setDraft(blankBranch);
      await refresh(platformKeys.branches(tenantId));
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  const input = (key: keyof BranchInput, label: string, hint?: string, mono = false) => (
    <Field label={label} hint={hint}>
      <input
        className={`${controlClass} ${mono ? 'font-mono' : ''}`}
        value={String(draft[key] ?? '')}
        onChange={set(key)}
      />
    </Field>
  );

  return (
    <Card>
      <SectionTitle icon="icon icon-store" hue="teal">
        Branches
      </SectionTitle>
      {(branches.data ?? []).length > 0 && (
        <ul className="mb-4 flex flex-col gap-1 ps-0">
          {branches.data!.map((b) => (
            <li
              key={b.id}
              className="flex list-none flex-wrap items-center gap-2 text-sm text-gray-900"
            >
              <span className="font-mono">{b.code}</span> {b.name}
              {b.isHeadOffice && <StatusBadge tone="brand">Head office</StatusBadge>}
              {b.gstin ? (
                <StatusBadge tone="neutral">{b.gstin}</StatusBadge>
              ) : (
                <StatusBadge tone="warning">No GSTIN</StatusBadge>
              )}
            </li>
          ))}
        </ul>
      )}
      <div {...enterKeyScope} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {input('code', 'Code', 'Short, e.g. HO.', true)}
        {input('name', 'Name')}
        {input('city', 'City')}
        {input(
          'stateCode',
          'State code',
          'Two digits, e.g. 24 for Gujarat. Decides CGST/SGST or IGST.',
          true,
        )}
        {input('stateName', 'State')}
        {input('gstin', 'GSTIN', 'Per branch; blank if unregistered.', true)}
        {input('addressLine1', 'Address')}
        {input('pincode', 'Pincode')}
        {input('contactPhone', 'Phone')}
        {!first && (
          <label className="flex items-center gap-2 text-sm text-gray-900 sm:col-span-3">
            <input type="checkbox" checked={draft.isHeadOffice} onChange={set('isHeadOffice')} />{' '}
            Head office
          </label>
        )}
        <div className="sm:col-span-3">
          <Button
            variant="primary"
            icon="icon icon-plus"
            data-enter-key-commit
            loading={busy}
            disabled={busy || !draft.code.trim() || !draft.name.trim()}
            onClick={() => void add()}
          >
            {busy ? 'Adding…' : first ? 'Add the head office' : 'Add branch'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ---- financial year & numbering ----------------------------------------------------------------

function YearAndNumberingSection({ tenantId }: { tenantId: number }) {
  const notify = useNotify();
  const refresh = useRefresh(tenantId);
  const years = useCompanyYears(tenantId, true);
  const branches = useCompanyBranches(tenantId, true);
  const numbering = useCompanyNumbering(tenantId, true);
  const [busy, setBusy] = useState(false);

  const current = years.data?.find((y) => y.isCurrent);
  const suggestion = suggestFinancialYear(new Date());

  const run = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await work();
      notify.success(done);
      await refresh(platformKeys.years(tenantId), platformKeys.numbering(tenantId));
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  const seriesAt = (branchId: number) =>
    (numbering.data ?? []).filter(
      (s) => s.branchId === branchId && s.financialYearId === current?.id,
    ).length;

  return (
    <Card>
      <SectionTitle icon="icon icon-hash" hue="cyan">
        Financial year and numbering
      </SectionTitle>
      {current ? (
        <p className="mb-3 text-sm text-gray-900">
          Current year <span className="font-mono">{current.code}</span> (
          {formatDate(current.startDate)} – {formatDate(current.endDate)}).
        </p>
      ) : (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span className="text-sm text-gray-900">
            No current year. Suggested: <span className="font-mono">{suggestion.code}</span>, April
            to March.
          </span>
          <Button
            variant="primary"
            loading={busy}
            disabled={busy}
            onClick={() =>
              void run(
                () => createFinancialYear(tenantId, { ...suggestion, isCurrent: true }),
                `${suggestion.code} created`,
              )
            }
          >
            Create {suggestion.code}
          </Button>
        </div>
      )}

      {current && (branches.data ?? []).length === 0 && (
        <p className="text-default text-sm">Add a branch first — numbering is per branch.</p>
      )}

      {current && (
        <ul className="flex flex-col gap-2 ps-0">
          {(branches.data ?? []).map((b) => (
            <li key={b.id} className="flex list-none flex-wrap items-center justify-between gap-2">
              <span className="text-sm text-gray-900">
                <span className="font-mono">{b.code}</span> — {seriesAt(b.id)} series for{' '}
                {current.code}
              </span>
              <Button
                disabled={busy}
                onClick={() =>
                  void run(
                    () => createStandardSeries(tenantId, b.id, current.id),
                    `Standard series ready at ${b.code}`,
                  )
                }
              >
                Create the standard series
              </Button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-default mt-3 text-[length:var(--scale-field-hint)]">
        Bookings, quotations, receipts, cleaning challans, both job cards, invoices, notes and
        expense vouchers. A series the shop already has is kept as it is.
      </p>
    </Card>
  );
}

// ---- catalogue ---------------------------------------------------------------------------------

function CatalogueSection({ tenantId }: { tenantId: number }) {
  const notify = useNotify();
  const refresh = useRefresh(tenantId);
  const template = useStarterCatalogue(tenantId, true);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      const result = await applyStarterCatalogue(tenantId);
      notify.success(
        result.categoriesAdded.length === 0 && result.valuesAdded === 0
          ? 'Already in place — nothing to add.'
          : `Added ${result.categoriesAdded.length} categories and ${result.valuesAdded} attribute values.`,
      );
      await refresh();
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  // Attribute names, not codes: the preview showed "shoe_size" until it was driven.
  const nameOf = (code: string) =>
    template.data?.attributes.find((a) => a.code === code)?.name.toLowerCase() ?? code;

  const tree = (categories: StarterCategory[]) => (
    <ul className="ps-4">
      {categories.map((c) => (
        <li key={c.code} className="list-none text-sm text-gray-900">
          {c.name}
          {c.attributes.length > 0 && (
            <span className="text-default"> · {c.attributes.map(nameOf).join(', ')}</span>
          )}
          {c.children.length > 0 && tree(c.children)}
        </li>
      ))}
    </ul>
  );

  return (
    <Card>
      <SectionTitle icon="icon icon-layers" hue="orange">
        Starter catalogue
      </SectionTitle>
      <p className="text-default mb-3 text-[length:var(--scale-field-hint)]">
        Categories with their sizes, colours and fit, ready to edit. No tax codes and no prices —
        those are the shop&apos;s. Anything the company already has is left as it is, so this is
        safe to press twice.
      </p>
      {template.data && <div className="-ms-4 mb-3">{tree(template.data.categories)}</div>}
      <Button
        variant="primary"
        loading={busy}
        disabled={busy || !template.data}
        onClick={() => void apply()}
      >
        {busy ? 'Applying…' : 'Apply the starter catalogue'}
      </Button>
      <p className="text-default mt-3 text-[length:var(--scale-field-hint)]">
        Designs and garments come from an Excel sheet — the next step, not built yet.
      </p>
    </Card>
  );
}

// ---- go live -----------------------------------------------------------------------------------

function GoLiveCard({
  tenantId,
  steps,
}: {
  tenantId: number;
  steps: { step: string; isComplete: boolean }[];
}) {
  const notify = useNotify();
  const refresh = useRefresh(tenantId);
  const [busy, setBusy] = useState(false);
  const outstanding = steps.filter((s) => s.step !== 'GoLive' && !s.isComplete);

  return (
    <Card>
      <SectionTitle icon="icon icon-rocket" hue="success">
        Go live
      </SectionTitle>
      {outstanding.length > 0 ? (
        <Notice
          tone="warning"
          title={`${outstanding.length} step${outstanding.length === 1 ? '' : 's'} still open`}
        >
          {outstanding.map((s) => stepLabels[s.step] ?? s.step).join(', ')}.
        </Notice>
      ) : (
        <p className="text-sm text-gray-900">Every setup step is done.</p>
      )}
      <p className="text-default my-3 text-[length:var(--scale-field-hint)]">
        Going live ends setup access: after it, this panel can no longer enter the company without
        the customer&apos;s consent. The date is kept for good.
      </p>
      {/*
        A labelled button, not ConfirmButton: that one is a row's icon-only action, and as the one
        irreversible step on this page it rendered as a small red rocket with no words — found by
        driving it. The confirmation is the same antd Popconfirm ConfirmButton wraps.
      */}
      <Popconfirm
        title="Go live and end setup access?"
        description="The panel will no longer be able to change this company's branches, year or catalogue."
        okText="Go live"
        okButtonProps={{ danger: true, loading: busy }}
        cancelText="Cancel"
        placement="topLeft"
        onConfirm={() => {
          setBusy(true);
          void goLive(tenantId)
            .then(async () => {
              dropSetupSession(tenantId);
              notify.success('The company is live.');
              await refresh();
            })
            .catch((error: unknown) => notify.error(error))
            .finally(() => setBusy(false));
        }}
      >
        <Button variant="primary" icon="icon icon-rocket" loading={busy} disabled={busy}>
          Go live
        </Button>
      </Popconfirm>
    </Card>
  );
}
